import {
  IconButton,
  Box,
  AppBar,
  useMediaQuery,
  Toolbar,
  styled,
  Stack,
  Tooltip,
} from '@mui/material';

import Profile from './Profile';

import Navigation from './Navigation';
import { RootState, useAppSelector } from '@shared/store';
import BreadcrumbV2 from '../../shared/breadcrumb/BreadcrumbV2';
import { useTranslation } from 'react-i18next';
import { generatePath, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconBack } from '@shared/assets/images/svgs';
import { AuthenticatedPath } from '@shared/constants/routes.constants';
import CampaignStatusButtonV2 from '@pages/Admin/Main/Campaign/ui/CampaignStatusButton/CampaignStatusButtonV2';
import { useStartStopContext } from '@features/Admin/Campaign/contexts/useStartStop.context';
import { SidebarTabNames } from '@pages/Admin/Campaign/ui/constants';

const Header = () => {
  const lgUp = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const { callStartStopFunction, campaignContext, isDisabledCampaignRun, disabledReason } =
    useStartStopContext();
  // drawer
  const BCrumb = useAppSelector((state: RootState) => state.breadcrumbs);
  const customizer = useAppSelector((state: RootState) => state.customizer);
  const isCallDetailPage = Boolean(
    matchPath({ path: AuthenticatedPath.CALL_DETAIL, end: true }, location.pathname),
  );
  const isCampaignEditHeader = customizer.isEditCampaignView && !isCallDetailPage;
  const callDetailBackPath = campaignId
    ? `${generatePath(AuthenticatedPath.EDIT_CAMPAIGN_V2, { campaignId })}?tab=${encodeURIComponent(
        SidebarTabNames.ONGOING_CALLS,
      )}`
    : AuthenticatedPath.CAMPAIGN;

  const AppBarStyled = styled(AppBar)(({ theme }) => ({
    boxShadow: 'none',
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: 'center',
    backdropFilter: 'blur(4px)',
    [theme.breakpoints.up('lg')]: {
      minHeight: customizer.TopbarHeight,
    },
  }));
  const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
    width: '100%',
    color: theme.palette.text.secondary,
  }));

  const BackButtonStyled = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    //TODO: move alfa-channeled color to palette
    boxShadow: `0px 2px 4px ${theme.palette.extendedGrey?.neutral}4B`,
    backgroundColor: 'transparent',
    border: `1px soled ${theme.palette.extendedGrey?.lighter}`,
    width: 30,
    height: 30,
    'svg path': {
      stroke: theme.palette.getContrastText(theme.palette.background.default),
    },
    '&:hover': {
      backgroundColor: theme.palette.extendedGrey?.lightest,
      'svg path': {
        stroke: theme.palette.primary.main,
      },
    },
  }));

  return (
    <AppBarStyled position="sticky" color="default">
      <ToolbarStyled>
        {isCampaignEditHeader || isCallDetailPage ? (
          <IconButton
            size="small"
            onClick={() =>
              navigate(isCampaignEditHeader ? AuthenticatedPath.CAMPAIGN : callDetailBackPath)
            }>
            <BackButtonStyled>
              <IconBack />
            </BackButtonStyled>
          </IconButton>
        ) : null}
        {BCrumb.iShown && (
          <BreadcrumbV2
            title={t(BCrumb.pageTitle, { defaultValue: '', ...BCrumb.titleValue })}
            campaignType={BCrumb.campaignType}
            icon={BCrumb.icon}
          />
        )}

        {/* ------------------------------------------- */}
        {/* Search Dropdown */}
        {/* ------------------------------------------- */}
        {/* <Search /> */}
        {lgUp ? (
          <>
            <Navigation />
          </>
        ) : null}

        <Box flexGrow={1} />
        <Stack spacing={1} direction="row" alignItems="center">
          {/* <Language /> */}
          {/* ------------------------------------------- */}
          {/* Ecommerce Dropdown */}
          {/* ------------------------------------------- */}
          {/* <Cart /> */}
          {/* ------------------------------------------- */}
          {/* End Ecommerce Dropdown */}
          {/* ------------------------------------------- */}
          {/* <Notifications /> */}
          {/* ------------------------------------------- */}
          {/* Toggle Right Sidebar for mobile */}
          {/* ------------------------------------------- */}
          {/* {lgDown ? <MobileRightSidebar /> : null} */}
          {!isCampaignEditHeader && <Profile />}
          {isCampaignEditHeader && campaignContext?.id && (
            <Tooltip
              title={isDisabledCampaignRun && disabledReason ? disabledReason : ''}
              arrow
              placement="bottom">
              <Box>
                <CampaignStatusButtonV2
                  minimized={false}
                  onClick={callStartStopFunction}
                  campaign={campaignContext}
                  disabled={isDisabledCampaignRun}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </ToolbarStyled>
    </AppBarStyled>
  );
};

export default Header;
